import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiOutlineSwatch, HiOutlinePhoto } from 'react-icons/hi2'

const API_URL = import.meta.env.VITE_API_URL

function Colors() {
  const { t, i18n } = useTranslation()
  const [colors, setColors] = useState([])
  const [paintings, setPaintings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/colors`).then(res => res.json()),
      fetch(`${API_URL}/paintings`).then(res => res.json())
    ])
      .then(([colorsData, paintingsData]) => {
        setColors(colorsData || [])
        setPaintings(paintingsData || [])
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // Helper: get paintings for a color
  const getPaintingsForColor = (colorId) => {
    return paintings.filter(p => Array.isArray(p.color_ids) && p.color_ids.includes(colorId))
  }

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
    </main>
  )

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {t('error.loading')}
      </div>
    </main>
  )

  return (
    <div className="min-h-screen w-screen bg-white dark:bg-zinc-900 transition-colors duration-300">
      <section className="w-full py-12 px-4 md:px-12 lg:px-24">
        <div className="w-full max-w-5xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('colors.title', 'Farben')}
          </h1>
          <div className="h-px w-16 bg-gray-300 dark:bg-zinc-700 mb-8" />
          {colors.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t("no_colors")}</p>
              <p className="text-sm text-gray-400">{t("add_first_color")}</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {colors.map((color) => {
              const colorPaintings = getPaintingsForColor(color.id)
              return (
                <Link
                  key={color.id}
                  to={`/colors/${color.id}`}
                  className="relative bg-white dark:bg-zinc-800 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 p-6 flex flex-col gap-2 items-start text-left focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-500"
                  style={{ 
                    border: color.name_de === 'Divers' || color.name_en === 'Diverse' 
                      ? '2px solid'
                      : `2px solid ${color.color_code}`,
                    borderImage: color.name_de === 'Divers' || color.name_en === 'Diverse'
                      ? 'linear-gradient(45deg, #ff6b6b, #ffd93d, #6c5ce7, #4ecdc4, #45b7d1, #96ceb4, #ffeead, #ff9a9e, #a8e6cf, #dcedc1) 1'
                      : 'none',
                    textDecoration: 'none'
                  }}
                  tabIndex={0}
                >
                  {/* Color Badge */}
                  <span className="absolute top-3 right-3 bg-pink-400 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow">
                    <HiOutlineSwatch className="inline-block" /> Color
                  </span>
                  {/* Color Swatch */}
                  <div 
                    className="w-16 h-16 rounded-lg mb-3 border-2 shadow"
                    style={{ 
                      background: color.name_de === 'Divers' || color.name_en === 'Diverse'
                        ? 'linear-gradient(45deg, #ff6b6b, #ffd93d, #6c5ce7, #4ecdc4, #45b7d1, #96ceb4, #ffeead, #ff9a9e, #a8e6cf, #dcedc1)'
                        : color.color_code,
                      borderColor: color.name_de === 'Divers' || color.name_en === 'Diverse'
                        ? '#e5e7eb'
                        : color.color_code
                    }} 
                    title={color.color_code}
                  ></div>
                  {/* Color Name */}
                  <h3 className="font-bold text-xl md:text-2xl text-gray-900 dark:text-gray-100 mb-1 mt-2">
                    {i18n.language === 'de' ? color.name_de : color.name_en}
                  </h3>
                  {/* Paintings Count */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <HiOutlinePhoto className="inline-block text-base align-middle" />
                    {colorPaintings.length} {t('colors.paintings', 'Werke')}
                  </div>
                  {/* Mini Thumbnails Vorschau */}
                  {colorPaintings.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {colorPaintings.slice(0, 3).map((painting) => {
                        const thumb = painting.images?.[0];
                        return thumb ? (
                          <Link
                            key={painting.id}
                            to={`/gallery/${painting.id}`}
                            className="block w-10 h-10 rounded overflow-hidden border border-white dark:border-zinc-700 shadow"
                            onClick={e => e.stopPropagation()}
                          >
                            <img
                              src={API_URL + (thumb.thumb_path || thumb.file_path)}
                              alt={painting.name}
                              className="w-full h-full object-cover"
                            />
                          </Link>
                        ) : null;
                      })}
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
          )}
        </div>
      </section>
    </div>
  )
}

const style = document.createElement('style');
style.textContent = `
  @keyframes rainbow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .rainbow-gradient {
    position: relative;
    overflow: hidden;
  }
  
  .rainbow-gradient::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, 
      rgba(255,255,255,0.1) 0%, 
      rgba(255,255,255,0.2) 50%, 
      rgba(255,255,255,0.1) 100%);
    animation: shine 2s infinite;
  }
  
  @keyframes shine {
    0% { transform: translateX(-100%) rotate(45deg); }
    100% { transform: translateX(100%) rotate(45deg); }
  }
`;
document.head.appendChild(style);

export default Colors 